import ClockCanvas from "./ClockCanvas";
import ParticlesRenderable from "./ParticlesRenderable";
import SimulationComputable from "./SimulationComputable";
import { requestWebGPU } from "~/src/utils";
import WebGPUCompute from "~/src/WebGPUCompute";
import WebGPURenderer from "~/src/WebGPURenderer";

const ENTITIES_COUNT = window.innerWidth * window.innerHeight;

const canvas = document.querySelector("canvas") as HTMLCanvasElement;
canvas.width = window.innerWidth * window.devicePixelRatio;
canvas.height = window.innerHeight * window.devicePixelRatio;

let renderer: WebGPURenderer;
let particlesRenderable: ParticlesRenderable;

let computer: WebGPUCompute;
let simulationComputable: SimulationComputable;

let device: GPUDevice;

let uniformBuffer;

const mouse = { x: -9999, y: -9999 };
let isMouseDown = false;
let entityCursor = 0;
let shouldSampleIndex = false;

const clockCanvas = new ClockCanvas();

const COLOURS = [
  [0.01, 0.85, 0.32, 1],
  [0.01, 0.85, 0.92, 1],
  [0.5, 0.95, 0.7, 1],
  [0.81, 0.25, 0.92, 1],
  [0.5, 0.1, 1, 1],
  [0.58, 0.33, 0.92, 1],
  [0.83, 0.9, 0.05, 1],
  [0.9, 0.7, 0.8, 1],
  [0.2, 0.92, 0.3, 1],
];
let colourIndex = 0;

const createSharedUniformBuffersAndBindGroups: () => [
  GPUBindGroupLayout,
  GPUBindGroup,
  GPUBuffer
] = () => {
  const uniformBufferSize =
    (2 * 2 + 1 * 4 + 1 * 4) * Float32Array.BYTES_PER_ELEMENT;

  const uniformBuffer = device.createBuffer({
    size: uniformBufferSize,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });

  const sharedUniformsBindGroupLayout = device.createBindGroupLayout({
    entries: [
      {
        binding: 0,
        visibility:
          GPUShaderStage.VERTEX |
          GPUShaderStage.FRAGMENT |
          GPUShaderStage.COMPUTE,
        buffer: { type: "uniform" },
      },
    ],
  });

  const sharedUniformsBindGroup = device.createBindGroup({
    layout: sharedUniformsBindGroupLayout,
    entries: [
      {
        binding: 0,
        resource: {
          buffer: uniformBuffer,
        },
      },
    ],
  });

  return [
    sharedUniformsBindGroupLayout,
    sharedUniformsBindGroup,
    uniformBuffer,
  ];
};

const onMouseDown = ({ clientX, clientY }) => {
  isMouseDown = true;
  mouse.x = clientX;
  mouse.y = clientY;
  colourIndex = (colourIndex + 1) % COLOURS.length;
};

const onMouseUp = () => {
  isMouseDown = false;
  mouse.x = -999999;
  mouse.y = -999999;
};

const onMouseMove = ({ clientX, clientY }) => {
  if (!isMouseDown) return;
  mouse.x = clientX;
  mouse.y = clientY;
};

const onKeyDown = ({ key }) => {
  if (COLOURS[key]) {
    colourIndex = parseInt(key);
  }
};

const animate = () => {
  const [r, g, b] = COLOURS[colourIndex];
  const uniformsArray = new Float32Array([
    window.innerWidth,
    window.innerHeight,
    mouse.x,
    mouse.y,
    r,
    g,
    b,
    1,
    shouldSampleIndex ? 1 : 0,
  ]);
  device.queue.writeBuffer(uniformBuffer, 0, uniformsArray);

  computer.compute();
  simulationComputable.swapBindGroups();

  particlesRenderable.simulationSrcBindGroup =
    simulationComputable.getActiveBindGroup();
  renderer.render();

  if (isMouseDown && entityCursor < ENTITIES_COUNT) {
    entityCursor++;
  }
  shouldSampleIndex = false;
  setTimeout(() => {
    requestAnimationFrame(animate);
  }, 16.66 * 2);
};

const init = async () => {
  device = (await requestWebGPU()) as GPUDevice;
  renderer = new WebGPURenderer(device, canvas);
  computer = new WebGPUCompute(device);

  const [sharedUniformsBindGroupLayout, sharedUniformsBindGroup, buffer] =
    createSharedUniformBuffersAndBindGroups();
  uniformBuffer = buffer;

  simulationComputable = new SimulationComputable(
    device,
    ENTITIES_COUNT,
    sharedUniformsBindGroupLayout,
    sharedUniformsBindGroup
  );
  computer.addComputable(simulationComputable);

  particlesRenderable = new ParticlesRenderable(
    device,
    renderer,
    ENTITIES_COUNT,
    sharedUniformsBindGroupLayout,
    sharedUniformsBindGroup,
    simulationComputable.getBindGroupLayout()
  );
  renderer.addRenderable(particlesRenderable);

  requestAnimationFrame(animate);
  window.addEventListener("mousedown", onMouseDown);
  window.addEventListener("mouseup", onMouseUp);
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("keydown", onKeyDown);

  setInterval(() => {
    const clockImageData = clockCanvas.getImageData();
    simulationComputable.updateInputMapBuffer(clockImageData);
    shouldSampleIndex = true;
    colourIndex = (colourIndex + 1) % COLOURS.length;
  }, 1000);
};

init();
